import React from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ChevronRight, ArrowRight } from 'lucide-react';
import type { SelfServiceSubmissionRow } from '../../types/selfService';
import { useSingleStationContext } from '../../components/self-service/admin/useAdminContext';
import { useSelfServicePublisher } from '../../components/self-service/auth/useSelfServicePublisher';
import { publisherStatusBadge } from '../../components/self-service/campaignSharedHelpers';
import {
  hasLibraryMissFailure,
  isSwapPending,
  needsPublisherReview,
} from '../../components/self-service/submissionReviewPredicates';
import MetricsSection from '../../components/self-service/metrics/MetricsSection';
import { apiClient } from '../../lib/apiClient';
import { displayedName } from '../../data/publisherConfigs';

type SubmissionRow = Pick<
  SelfServiceSubmissionRow,
  | 'id'
  | 'business_info'
  | 'contact_email'
  | 'campaign'
  | 'amount_cents'
  | 'status'
  | 'created_at'
  | 'creatives'
  | 'pushJobFailure'
>;

const PublisherAdminPage: React.FC = () => {
  const { slug, config } = useSingleStationContext();
  const { row: publisherRow } = useSelfServicePublisher(slug);
  const [submissions, setSubmissions] = React.useState<SubmissionRow[]>([]);

  React.useEffect(() => {
    (async () => {
      try {
        const { rows } = await apiClient.post<{ rows: SubmissionRow[] }>(
          '/submissions/list-by-publisher',
          { slug },
        );
        setSubmissions(rows ?? []);
      } catch {
        /* non-fatal — overview tiles tolerate empty list */
      }
    })();
  }, [slug]);

  // CTV-518: CTVBuyer is merchant of record — checkout is NOT gated on the
  // publisher's Stripe state. The only payout-side signal that matters here
  // is whether transfers can reach the publisher (payouts readiness).
  const payoutsConnected = publisherRow?.stripe_payouts_enabled;
  const needsReview = submissions.filter(needsPublisherReview);
  // Recent submissions: drop drafts (not actionable) and rows already surfaced
  // in Needs your review (avoid double-rendering).
  const recent = submissions
    .filter((s) => s.status !== 'pending_payment' && !needsPublisherReview(s))
    .slice(0, 5);

  return (
    <div className="max-w-5xl">
      <h1 className="text-2xl font-semibold text-slate-900 mb-1">Overview</h1>
      <p className="text-sm text-slate-600 mb-6">Welcome, {displayedName(config)}.</p>

      {!payoutsConnected && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-semibold text-amber-900 text-sm mb-1">Connect a payout method to receive your share of campaign revenue</div>
            <p className="text-sm text-amber-800 mb-3">Advertisers can already book and pay for campaigns — CTVBuyer collects payment and holds your share until a payout method is connected.</p>
            <Link to={`/admin/${slug}/payments`} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-900 hover:bg-amber-950 text-white text-sm font-medium rounded-md">
              Set up payouts <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      )}

      <MetricsSection slug={slug} />

      {needsReview.length > 0 && (
        <section className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
              Needs your review · {needsReview.length}
            </h2>
            <Link
              to={`/admin/${slug}/submissions?tab=review`}
              className="text-sm text-blue-600 hover:underline"
            >
              View all →
            </Link>
          </div>
          <ul className="space-y-2">
            {needsReview.map((s) => (
              <SubmissionListRow key={s.id} s={s} slug={slug} />
            ))}
          </ul>
        </section>
      )}

      {recent.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Recent submissions</h2>
            <Link to={`/admin/${slug}/submissions`} className="text-sm text-blue-600 hover:underline">View all →</Link>
          </div>
          <ul className="space-y-2">
            {recent.map((s) => (
              <SubmissionListRow key={s.id} s={s} slug={slug} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

function SubmissionListRow({ s, slug }: { s: SubmissionRow; slug: string }) {
  const badge = publisherStatusBadge(s.status);
  return (
    <li>
      <Link
        to={`/admin/${slug}/submissions/${s.id}`}
        className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl p-3 hover:border-slate-400 hover:shadow-sm transition-all"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-sm font-medium text-slate-900 truncate">
              {s.business_info?.businessName || 'Advertiser'}
            </span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded ${badge.className}`}>
              {badge.label}
            </span>
            {isSwapPending(s) && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
                Creative swap
              </span>
            )}
            {hasLibraryMissFailure(s) && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-50 text-red-800 border border-red-200">
                Push failed — creative not in library
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500">
            ${(s.amount_cents / 100).toFixed(2)} · {new Date(s.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-400" />
      </Link>
    </li>
  );
}

export default PublisherAdminPage;
